/**
 * 編輯單一媒體 caption 的 form（不含 Modal 殼，不含按鈕）。
 *
 * 由 EditMediaModal 與 PostEditModal sub-view 共用。
 * 儲存/取消按鈕統一放在呼叫端的 Modal.Footer（全站 modal 慣例），
 * 本元件只負責欄位，透過 ref 暴露 save()，並用 onValidityChange 回報是否可儲存。
 */
import { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

const MAX_CAPTION = 255;

const EditMediaForm = forwardRef(({ media, initialCaption = '', onSave, onValidityChange }, ref) => {
  const { t } = useTranslation();
  const [caption, setCaption] = useState('');
  const [videoUnsupported, setVideoUnsupported] = useState(false);

  useEffect(() => { setVideoUnsupported(false); }, [media?.url]);

  useEffect(() => {
    setCaption(initialCaption);
  }, [initialCaption, media]);

  const canSave = caption.length <= MAX_CAPTION;

  useEffect(() => {
    onValidityChange?.(canSave);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canSave]);

  useImperativeHandle(ref, () => ({
    save: () => {
      if (!media || !canSave) return;
      onSave?.(media.index, caption);
    },
  }));

  if (!media) return null;

  return (
    <>
      <div className="mb-3">
        {media.type === 1 ? (
          media.uploading ? (
            <div className="text-center py-5">
              <div className="spinner-border mb-3" role="status" />
              <p className="text-muted mb-0">{t('video_uploading')}</p>
            </div>
          ) : media.playback_url ? (
            <img
              src={media.thumbnail_url}
              alt="Video thumbnail"
              className="img-fluid w-100"
              style={{ maxHeight: '400px', objectFit: 'contain', backgroundColor: '#000' }}
            />
          ) : (
            <>
              <video
                src={media.url}
                poster={media.thumbnail_url}
                controls
                className="img-fluid w-100"
                style={{ maxHeight: '400px', objectFit: 'contain' }}
                onError={(e) => {
                  if (e.target.error?.code === MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED) {
                    setVideoUnsupported(true);
                  }
                }}
              />
              {videoUnsupported && (
                <p className="text-center text-muted mt-2 mb-0 fs-12">
                  {t('video_preview_unsupported')}
                </p>
              )}
            </>
          )
        ) : (
          <img
            src={media.big_url || media.url}
            alt="Media preview"
            className="img-fluid w-100"
            style={{ maxHeight: '400px', objectFit: 'contain' }}
          />
        )}
      </div>

      <Form.Group className="mb-3">
        <Form.Control
          as="textarea"
          rows={3}
          placeholder={t('caption')}
          value={caption}
          onChange={(e) => {
            const v = e.target.value;
            if (v.length <= MAX_CAPTION) setCaption(v);
          }}
          className="border-0 bg-light"
          style={{ resize: 'none' }}
          maxLength={MAX_CAPTION}
        />
        <small className="text-muted d-block mt-1">
          {caption.length}/{MAX_CAPTION} {t('characters')}
        </small>
      </Form.Group>
    </>
  );
});

EditMediaForm.displayName = 'EditMediaForm';

export default EditMediaForm;
